import React, { useEffect } from "react";
import { useProject } from "../../state/ProjectContext";
import EditableTable from "./_EditableTable";
import { GitBranch, Users2, FileText } from "lucide-react";

const VARIANTS = [
  { id: "greenfield",   label: "Greenfield",              desc: "New implementation · 100% clean core · SAP Best Practices adoption" },
  { id: "brownfield",   label: "Brownfield / Conversion", desc: "System conversion · lowest disruption · historical data preserved" },
  { id: "bluefield",    label: "Selective Data Transition (Bluefield)", desc: "Hybrid · clean S/4 + selective master & open items" },
  { id: "rollout",      label: "Global Rollout",          desc: "Template deployment across regions / entities" },
  { id: "optimization", label: "Business / Process Optimization", desc: "No re-platforming · pure process & value improvement" },
];

// Default RACI rows per delivery methodology. Seeded automatically the first
// time Governance is opened on a project so users start from a sensible
// baseline instead of a blank table. Users can freely edit / add / delete.
const RACI_DEFAULTS = {
  Waterfall: [
    { workstream: "Program Management",       responsible: "PMO",              accountable: "Steering Committee", consulted: "Sponsor",            informed: "Business Leads" },
    { workstream: "Solution Architecture",    responsible: "Solution Architect", accountable: "PMO",             consulted: "Business SMEs",      informed: "Delivery Team" },
    { workstream: "Requirements & Design",    responsible: "Business Analyst", accountable: "Solution Architect", consulted: "Process Owners",     informed: "Sponsor" },
    { workstream: "Build & Configuration",    responsible: "Delivery Lead",    accountable: "PMO",                consulted: "Solution Architect", informed: "Business Leads" },
    { workstream: "Testing (SIT/UAT)",        responsible: "Test Lead",        accountable: "PMO",                consulted: "Business Users",     informed: "Sponsor" },
    { workstream: "Go-Live & Hypercare",      responsible: "Cutover Lead",     accountable: "Steering Committee", consulted: "Operations",         informed: "All Stakeholders" },
  ],
  Agile: [
    { workstream: "Product Vision & Roadmap", responsible: "Product Owner",    accountable: "Sponsor",            consulted: "Business Stakeholders", informed: "Delivery Team" },
    { workstream: "Backlog Refinement",       responsible: "Product Owner",    accountable: "Scrum Master",       consulted: "Dev Team",            informed: "Stakeholders" },
    { workstream: "Sprint Delivery",          responsible: "Scrum Master",     accountable: "Product Owner",      consulted: "Dev Team",            informed: "Business Leads" },
    { workstream: "Ceremonies (Standup/Review)", responsible: "Scrum Master", accountable: "Product Owner",      consulted: "Team",                informed: "Stakeholders" },
    { workstream: "Quality & Continuous Testing", responsible: "QA Engineer", accountable: "Scrum Master",       consulted: "Dev Team",            informed: "Product Owner" },
    { workstream: "Retrospective & Improve",  responsible: "Scrum Master",     accountable: "Team",               consulted: "Coach",               informed: "Sponsor" },
  ],
  Hybrid: [
    { workstream: "Program Governance",       responsible: "PMO",              accountable: "Steering Committee", consulted: "Sponsor",            informed: "Business Leads" },
    { workstream: "Chapter / Guild Leadership", responsible: "Chapter Lead",   accountable: "PMO",                consulted: "Solution Architect", informed: "Dev Team" },
    { workstream: "Solution Architecture",    responsible: "Solution Architect", accountable: "PMO",             consulted: "Product Owner",      informed: "Team" },
    { workstream: "Requirements → Backlog",   responsible: "Business Analyst", accountable: "Product Owner",      consulted: "Process Owners",     informed: "PMO" },
    { workstream: "Sprint / Wave Delivery",   responsible: "Scrum Master",     accountable: "Product Owner",      consulted: "Dev Team",            informed: "PMO" },
    { workstream: "Release & Cutover",        responsible: "Cutover Lead",     accountable: "PMO",                consulted: "Product Owner",       informed: "Business Leads" },
  ],
};

export default function Governance() {
  const { doc, mutate, readOnly } = useProject();
  const variant = doc?.governance?.variant || "greenfield";
  const methodology = doc?.meta?.methodology || "Waterfall";

  const setVariant = (v) => mutate(d => ({ ...d, governance: { ...(d.governance || {}), variant: v } }));

  // Seed default RACI on first visit if the table is empty for the current
  // methodology. Non-destructive: never overwrites user edits.
  useEffect(() => {
    if (readOnly) return;
    const raci = doc?.governance?.raci;
    if (Array.isArray(raci) && raci.length > 0) return;
    const seed = RACI_DEFAULTS[methodology] || RACI_DEFAULTS.Waterfall;
    mutate(d => ({
      ...d,
      governance: {
        ...(d.governance || {}),
        raci: seed.map((r, i) => ({ id: `raci-${methodology}-${i + 1}`, ...r })),
        raciSeededFor: methodology,
      },
    }));
    // Ensures we only re-seed when methodology changes, not on every render.
  }, [methodology]);

  const reseedRaci = () => {
    if (!window.confirm(`Reset RACI to ${methodology} defaults? This overwrites your current rows.`)) return;
    const seed = RACI_DEFAULTS[methodology] || RACI_DEFAULTS.Waterfall;
    mutate(d => ({
      ...d,
      governance: {
        ...(d.governance || {}),
        raci: seed.map((r, i) => ({ id: `raci-${methodology}-${i + 1}`, ...r })),
        raciSeededFor: methodology,
      },
    }));
  };

  return (
    <>
      <div className="mb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 section-eyebrow">Program & Project Governance Core</div>
        <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-zinc-900 mt-1">Governance</h2>
        <p className="text-sm text-zinc-500 mt-1 max-w-3xl">Delivery variant · RACI & org · Change Request log · Steering committee tracking.</p>
      </div>

      {/* Activate Variant */}
      <div className="card p-5 mb-5" data-export-capture="governance-variant" data-export-title="Governance · Delivery Variant">
        <div className="flex items-center gap-2 mb-3">
          <GitBranch size={14} className="text-zinc-500"/>
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">Activate delivery variant</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {VARIANTS.map(v => (
            <button
              key={v.id}
              onClick={() => !readOnly && setVariant(v.id)}
              className={`text-left p-3 rounded-sm border transition-all ${variant === v.id ? "border-zinc-900 bg-zinc-50" : "border-[var(--border)] hover:border-zinc-400"}`}
              data-testid={`governance-variant-${v.id}`}
            >
              <div className="font-medium text-sm text-zinc-900">{v.label}</div>
              <div className="text-[11px] text-zinc-500 mt-1 leading-relaxed">{v.desc}</div>
              {variant === v.id && <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-zinc-900 mt-2">Selected</div>}
            </button>
          ))}
        </div>
      </div>

      {/* RACI matrix */}
      <div className="mb-5">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <Users2 size={14} className="text-zinc-500"/>
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">RACI matrix · Workstream ownership</div>
          <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-cyan-700 ml-1">· {methodology}</span>
          {!readOnly && (
            <button className="btn btn-ghost btn-sm ml-auto text-[10px]" onClick={reseedRaci} data-testid="btn-reseed-raci" title={`Reset RACI to ${methodology} defaults`}>
              Reset to {methodology} defaults
            </button>
          )}
        </div>
        <EditableTable
          sectionKey="governance"
          listKey="raci"
          testidPrefix="raci"
          labelSingular="row"
          columns={[
            { key: "workstream", label: "Workstream", width: "180px" },
            { key: "responsible", label: "R · Responsible" },
            { key: "accountable", label: "A · Accountable" },
            { key: "consulted", label: "C · Consulted" },
            { key: "informed", label: "I · Informed" },
          ]}
          emptyRow={() => ({ workstream: "", responsible: "", accountable: "", consulted: "", informed: "" })}
        />
      </div>

      {/* Change Request log */}
      <div className="mb-5">
        <div className="flex items-center gap-2 mb-2">
          <FileText size={14} className="text-zinc-500"/>
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">Change Request log · Impact & approval</div>
        </div>
        <EditableTable
          sectionKey="governance"
          listKey="crLog"
          testidPrefix="cr"
          labelSingular="CR"
          columns={[
            { key: "ref", label: "Ref", width: "80px" },
            { key: "title", label: "Title" },
            { key: "scopeImpact", label: "Scope impact" },
            { key: "scheduleDays", label: "Δ Days", type: "number", width: "70px" },
            { key: "budgetDelta", label: "Δ Budget $", type: "number", width: "100px" },
            { key: "status", label: "Status", type: "select", options: ["Draft", "Submitted", "Under Review", "Approved", "Rejected", "Implemented"], width: "120px" },
            { key: "owner", label: "Owner", width: "120px" },
          ]}
          emptyRow={() => ({ ref: `CR-${String(((doc?.governance?.crLog || []).length || 0) + 1).padStart(3, "0")}`, title: "", scopeImpact: "", scheduleDays: 0, budgetDelta: 0, status: "Draft", owner: "" })}
        />
      </div>

      {/* Steering committee cadence */}
      <div className="mb-5">
        <div className="flex items-center gap-2 mb-2">
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">Steering committee · Meeting log</div>
        </div>
        <EditableTable
          sectionKey="governance"
          listKey="steerCo"
          testidPrefix="steerco"
          labelSingular="meeting"
          columns={[
            { key: "date", label: "Date", type: "date", width: "140px" },
            { key: "topic", label: "Topic" },
            { key: "decisions", label: "Decisions" },
            { key: "escalations", label: "Escalations" },
            { key: "nextActions", label: "Next actions" },
          ]}
          emptyRow={() => ({ date: new Date().toISOString().split("T")[0], topic: "", decisions: "", escalations: "", nextActions: "" })}
        />
      </div>
    </>
  );
}
